import { useMemo, useState } from "react";
import { Flame, Star, RotateCcw, Zap, Swords, Users, UsersRound, Coffee, Utensils, PartyPopper, Laugh, Trophy, PenLine, Bell, BarChart3, Newspaper } from "lucide-react";
import { TeamMark } from "@/components/Bits";

export const PRESETS = {
  detente: { label: "Détente", ok: 3, ko: 0, bonus: [[3, 3]], extra: "Aucun point perdu. Idéal pour débuter." },
  competition: { label: "Compétition", ok: 5, ko: -3, bonus: [[3, 5], [5, 10]], extra: "+ jusqu'à 2 questions bonus par match (meilleur marqueur, total de points…)." },
  expert: { label: "Expert", ok: 5, ko: -5, bonus: [[3, 8]], extra: "+ jusqu'à 3 questions bonus par match. Chaque erreur coûte cher." },
};
const GAMES = [
  { away: "BOS", home: "NYK", win: "home" }, { away: "LAL", home: "DEN", win: "away" }, { away: "GSW", home: "OKC", win: "home", featured: true },
  { away: "MIA", home: "PHI", win: "home" }, { away: "DAL", home: "MIL", win: "away" },
];
const FEATURED_PTS = 5;

function score(picks, p, featuredOn) {
  let streak = 0, total = 0;
  const rows = GAMES.map((g, i) => {
    const pick = picks[i];
    if (!pick) return { i, skip: true, pts: 0 };
    if (pick !== g.win) { streak = 0; total += p.ko; return { i, ok: false, pts: p.ko }; }
    streak += 1;
    const b = p.bonus.find(([at]) => at === streak)?.[1] || 0;
    const f = g.featured && featuredOn ? FEATURED_PTS : 0;
    total += p.ok + b + f;
    return { i, ok: true, pts: p.ok + b + f, bonus: b, featured: f, streak };
  });
  return { rows, total };
}

function Pick({ g, i, pick, setPick, revealed, row }) {
  const btn = (side) => {
    const on = pick === side, winner = revealed && g.win === side;
    return (
      <button type="button" disabled={revealed} onClick={() => setPick(i, on ? null : side)} aria-pressed={on} data-testid={`sim-pick-${i}-${side}`}
        className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-2 py-2.5 text-sm font-bold transition-colors ${on ? "border-[#B7F52A] bg-[#B7F52A]/15 text-white" : "border-white/10 text-slate-300 hover:border-white/30"} ${winner ? "ring-1 ring-[#B7F52A]/60" : ""}`}>
        <TeamMark team={{ abbr: g[side] }} size={26} /> {g[side]}
      </button>
    );
  };
  return (
    <li className="flex items-center gap-2" data-testid={`sim-game-${i}`}>
      <span className="w-5 shrink-0 text-center">{g.featured ? <Star size={14} className="mx-auto text-[#B7F52A]" fill="currentColor" aria-label="Match vedette" /> : <span className="text-xs text-slate-600">{i + 1}</span>}</span>
      {btn("away")}<span className="text-xs text-slate-600">@</span>{btn("home")}
      <span className={`w-14 shrink-0 text-right font-mono-num text-sm font-bold ${!revealed ? "text-transparent" : row.skip ? "text-slate-500" : row.ok ? "text-[#B7F52A]" : "text-red-300"}`} data-testid={`sim-pts-${i}`}>
        {revealed ? (row.skip ? "0" : `${row.pts > 0 ? "+" : ""}${row.pts}`) : "·"}
      </span>
    </li>
  );
}

export function Simulator() {
  const [preset, setPreset] = useState("competition");
  const [picks, setPicks] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [featuredOn, setFeaturedOn] = useState(true);
  const p = PRESETS[preset];
  const res = useMemo(() => score(picks, p, featuredOn), [picks, p, featuredOn]);
  const setPick = (i, v) => setPicks((s) => ({ ...s, [i]: v }));
  const reset = () => { setPicks({}); setRevealed(false); };
  const lines = res.rows.filter((r) => !r.skip);
  return (
    <section id="simulateur" className="relative z-10 mx-auto max-w-6xl scroll-mt-20 px-5 py-16" data-testid="landing-simulator">
      <p className="mpl-eyebrow text-[#B7F52A]">Simulateur de points</p>
      <h2 className="mt-2 font-display text-4xl font-extrabold uppercase sm:text-5xl">Fais ta soirée. Compte tes points.</h2>
      <p className="mt-3 max-w-2xl text-sm text-slate-400">Choisis un vainqueur pour chaque match, puis révèle les résultats. Matchs et résultats d'exemple, pour comprendre le calcul.</p>
      <div className="mt-8 grid gap-5 lg:grid-cols-[1.3fr_1fr]">
        <div className="mpl-card p-4 sm:p-5">
          <div className="mb-4 flex gap-1 rounded-xl bg-white/[0.04] p-1" role="tablist">
            {Object.entries(PRESETS).map(([k, v]) => (
              <button key={k} role="tab" aria-selected={preset === k} onClick={() => setPreset(k)} data-testid={`sim-preset-${k}`}
                className={`flex-1 rounded-lg py-2 text-xs font-bold uppercase tracking-wide transition-colors ${preset === k ? "bg-[#B7F52A] text-[#101310]" : "text-slate-400 hover:text-white"}`}>{v.label}</button>))}
          </div>
          <ol className="space-y-2">{GAMES.map((g, i) => <Pick key={i} g={g} i={i} pick={picks[i]} setPick={setPick} revealed={revealed} row={res.rows[i]} />)}</ol>
          <label className="mt-4 flex items-center gap-2 text-xs text-slate-400">
            <input type="checkbox" checked={featuredOn} onChange={(e) => setFeaturedOn(e.target.checked)} className="accent-[#B7F52A]" data-testid="sim-featured-toggle" />
            <Star size={12} className="text-[#B7F52A]" /> Match vedette activé dans la ligue (+{FEATURED_PTS} pts si bon prono)
          </label>
          <div className="mt-4 flex gap-2">
            {!revealed ? <button onClick={() => setRevealed(true)} disabled={!lines.length} className="mpl-btn-primary flex-1" data-testid="sim-reveal">Révéler les résultats</button>
              : <button onClick={reset} className="mpl-btn-ghost flex-1" data-testid="sim-reset"><RotateCcw size={14} /> Rejouer</button>}
          </div>
        </div>
        <SimResult p={p} res={res} revealed={revealed} count={lines.length} />
      </div>
    </section>
  );
}

function SimResult({ p, res, revealed, count }) {
  const ok = res.rows.filter((r) => r.ok), ko = res.rows.filter((r) => r.ok === false);
  const bonus = ok.reduce((s, r) => s + r.bonus, 0), feat = ok.reduce((s, r) => s + r.featured, 0);
  return (
    <aside className="mpl-card flex flex-col p-5" data-testid="sim-result">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Règles {p.label}</p>
      <ul className="mt-2 space-y-1.5 text-sm">
        <li className="flex justify-between"><span>Bon vainqueur</span><b className="font-mono-num text-[#B7F52A]">+{p.ok}</b></li>
        <li className="flex justify-between"><span>Mauvais vainqueur</span><b className={`font-mono-num ${p.ko ? "text-red-300" : "text-slate-400"}`}>{p.ko || "0"}</b></li>
        {p.bonus.map(([at, pts]) => <li key={at} className="flex justify-between"><span className="flex items-center gap-1"><Flame size={13} className="text-orange-300" /> Série de {at} bons pronos</span><b className="font-mono-num text-[#B7F52A]">+{pts}</b></li>)}
        <li className="flex justify-between text-slate-400"><span>Match non pronostiqué</span><b className="font-mono-num">0</b></li>
      </ul>
      <p className="mt-2 text-xs text-slate-500">{p.extra}</p>
      <div className="mt-auto border-t border-white/[0.08] pt-4">
        {revealed ? (
          <>
            <p className="text-xs text-slate-400" data-testid="sim-breakdown">{ok.length} bon{ok.length > 1 ? "s" : ""} ({ok.length * p.ok > 0 ? "+" : ""}{ok.length * p.ok}) · {ko.length} raté{ko.length > 1 ? "s" : ""} ({ko.length * p.ko}){bonus ? ` · série +${bonus}` : ""}{feat ? ` · vedette +${feat}` : ""}</p>
            <p className="mt-1 font-display text-6xl font-extrabold" data-testid="sim-total">{res.total > 0 ? "+" : ""}{res.total} <span className="text-xl text-slate-400">pts</span></p>
          </>
        ) : <p className="text-sm text-slate-400" data-testid="sim-waiting">{count ? `${count} prono${count > 1 ? "s" : ""} sur 5. Révèle quand tu veux.` : "Commence par choisir un vainqueur."}</p>}
      </div>
    </aside>
  );
}

const KINDS = [
  { icon: Users, title: "Collectif", text: "Toute la ligue joue. Le dernier (ou le premier) de la période relève le défi." },
  { icon: Swords, title: "1 contre 1", text: "Un duel entre deux membres. Celui qui fait le plus de points gagne." },
  { icon: UsersRound, title: "De groupe", text: "Une activité à organiser ensemble : soirée NBA, finale à regarder…" },
];
const CATS = [
  { icon: Coffee, title: "Sorties", ex: "Le dernier apporte les cafés" }, { icon: Utensils, title: "Repas", ex: "Le dernier organise le prochain resto (chacun paie sa part)" },
  { icon: PartyPopper, title: "Activités", ex: "Le champion choisit la prochaine sortie" }, { icon: Laugh, title: "Amusants", ex: "Badge de la lanterne rouge pour un mois" },
  { icon: Trophy, title: "Récompenses", ex: "Titre « Oracle de la ligue » pour la saison" }, { icon: PenLine, title: "Libre", ex: "Ta ligue invente son propre défi" },
];
const FLOW = ["Un membre propose", "Chacun accepte (ou refuse)", "La période se joue", "Le classement désigne le gagnant", "Gage fait… ou revanche"];

export function ChallengeTypes() {
  return (
    <section className="relative z-10 mx-auto max-w-6xl px-5 py-16" data-testid="landing-challenges">
      <p className="mpl-eyebrow text-[#B7F52A]">Les défis</p>
      <h2 className="mt-2 font-display text-4xl font-extrabold uppercase sm:text-5xl">Ce qui se joue vraiment.</h2>
      <p className="mt-3 max-w-2xl text-sm text-slate-400">Sur une semaine, un mois, un trimestre ou toute la saison. Personne n'est engagé sans avoir accepté, et il existe toujours une option sans dépense.</p>
      <div className="mt-8 grid gap-3 md:grid-cols-3">{KINDS.map((k) => (
        <div key={k.title} className="mpl-card p-5" data-testid={`landing-kind-${k.title}`}><k.icon size={22} className="text-[#B7F52A]" /><h3 className="mt-3 font-bold">{k.title}</h3><p className="mt-1 text-sm text-slate-400">{k.text}</p></div>))}</div>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{CATS.map((c) => (
        <li key={c.title} className="mpl-card flex items-start gap-3 p-4 transition-colors hover:border-[#B7F52A]/40" data-testid={`landing-cat-${c.title}`}>
          <c.icon size={18} className="mt-0.5 shrink-0 text-[#B7F52A]" /><span><b className="block text-sm">{c.title}</b><span className="text-xs text-slate-400">« {c.ex} »</span></span></li>))}</ul>
      <ol className="mt-8 flex flex-col gap-2 sm:flex-row sm:items-center" data-testid="landing-challenge-flow">{FLOW.map((f, i) => (
        <li key={f} className="flex flex-1 items-center gap-2 text-sm"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#B7F52A] font-display text-sm font-extrabold text-[#101310]">{i + 1}</span><span className="text-slate-300">{f}</span></li>))}</ol>
    </section>
  );
}

const FEATURES = [
  { icon: Zap, title: "Pronos rapides", text: "Tous les matchs du soir sur un écran, un appui par match." },
  { icon: Star, title: "Match vedette", text: "Chaque jour, un choc mis en avant qui rapporte des points en plus." },
  { icon: Swords, title: "Face-à-face", text: "Qui gagne quand vous n'avez pas fait le même choix ? L'app tient les comptes." },
  { icon: Flame, title: "Rival de la semaine", text: "Chaque lundi, le joueur à rattraper (ou à tenir derrière toi)." },
  { icon: Newspaper, title: "Récap du lundi", text: "Sans-faute, coup de génie, plus forte remontée : la semaine en un coup d'œil." },
  { icon: Bell, title: "Alertes au bon moment", text: "Rappel les jours de match, résultats, mentions. Tu choisis lesquelles." },
];

export function Features() {
  return (
    <section className="relative z-10 mx-auto max-w-6xl px-5 py-16" data-testid="landing-features">
      <p className="mpl-eyebrow text-[#B7F52A]">Dans l'app</p>
      <h2 className="mt-2 font-display text-4xl font-extrabold uppercase sm:text-5xl">Tout pour chambrer proprement.</h2>
      <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{FEATURES.map((f) => (
        <li key={f.title} className="mpl-card group p-5 transition-transform duration-300 hover:-translate-y-1" data-testid={`landing-feature-${f.title}`}>
          <f.icon size={20} className="text-[#B7F52A] transition-transform duration-300 group-hover:scale-110" /><h3 className="mt-3 font-bold">{f.title}</h3><p className="mt-1 text-sm text-slate-400">{f.text}</p></li>))}</ul>
      <p className="mt-3 flex items-center gap-2 text-xs text-slate-500"><BarChart3 size={13} /> Résultats NBA réels, mis à jour automatiquement. Classement par semaine, mois, saison.</p>
    </section>
  );
}

